import { domainConfig } from '../config/domain.config';

/**
 * Hosts that run the Hi-Level marketing GTM container (GTM-5DS7VZ9) and
 * therefore carry the `iqd-budget-calc` postMessage listener tag. Only these
 * parents receive analytics events from the calculator.
 */
const BRIDGE_HOSTS = ['www.hi-levelmezzanines.co.uk', 'hi-level.wp1.site'];

export interface EmbeddingInfo {
  parentDomain: string;
  isEmbedded: boolean;
  allowedDomain: boolean;
  /** Full origin of the embedding page, when it can be determined. */
  parentOrigin: string | null;
}

function isDomainAllowed(domain: string, allowedDomains: string[]): boolean {
  if (!domain) return false;
  if (allowedDomains.includes(domain)) return true;
  return allowedDomains.some((allowed) => {
    if (allowed.startsWith('*.')) {
      const base = allowed.substring(2);
      return domain.endsWith(base);
    }
    return domain.endsWith('.' + allowed);
  });
}

/**
 * The parent origin is only readable cross-origin via the referrer, which the
 * embedding page can suppress with a referrer policy. Returns null when it
 * cannot be established — callers must not fall back to a wildcard target.
 */
function readParentOrigin(): string | null {
  if (!document.referrer) return null;
  try {
    return new URL(document.referrer).origin;
  } catch {
    return null;
  }
}

function detect(): EmbeddingInfo {
  try {
    let parentDomain = '';
    let parentOrigin: string | null = null;
    let isEmbedded = false;

    if (window.self !== window.top) {
      isEmbedded = true;
      try {
        parentDomain = window.top?.location.hostname ?? '';
        parentOrigin = window.top?.location.origin ?? null;
      } catch {
        // Cross-origin parent: fall back to the referrer.
        parentOrigin = readParentOrigin();
        if (parentOrigin) parentDomain = new URL(parentOrigin).hostname;
      }
    } else {
      parentDomain = window.location.hostname;
      parentOrigin = window.location.origin;
    }

    const allowedDomain = isDomainAllowed(parentDomain, domainConfig.allowedDomains);

    if (domainConfig.logDomainInfo) {
      console.log('Domain Tracking Info:', {
        parentDomain,
        parentOrigin,
        isEmbedded,
        allowedDomain,
        currentDomain: window.location.hostname,
        referrer: document.referrer,
      });
    }

    return { parentDomain, parentOrigin, isEmbedded, allowedDomain };
  } catch (error) {
    console.error('Error detecting embedding domain:', error);
    return {
      parentDomain: window.location.hostname,
      parentOrigin: window.location.origin,
      isEmbedded: false,
      allowedDomain: true,
    };
  }
}

let cached: EmbeddingInfo | null = null;

/** Detection runs once per page load; the result cannot change afterwards. */
export function getEmbeddingInfo(): EmbeddingInfo {
  if (cached === null) cached = detect();
  return cached;
}

/**
 * The origin to postMessage analytics events to, or null when the calculator
 * is not embedded on a host that listens for them.
 */
export function getBridgeOrigin(): string | null {
  const info = getEmbeddingInfo();
  if (!info.isEmbedded || !info.parentOrigin) return null;
  let host: string;
  try {
    host = new URL(info.parentOrigin).hostname;
  } catch {
    return null;
  }
  return BRIDGE_HOSTS.includes(host) ? info.parentOrigin : null;
}
